import React, { useState, useEffect, useRef } from 'react';
import useData from '../../hooks/useData';
import useToast from '../../hooks/useToast';
import GlassButton from '../ui/Button';
import { DeleteIcon } from '../ui/Icons';

const CommentsWidget = ({ task, onDeleteRequest }) => {
    const { currentUserProfile, handleAddComment, fetchUserProfiles, userProfiles } = useData();
    const { showToast } = useToast();
    const [newComment, setNewComment] = useState('');
    const commentsEndRef = useRef(null);

    const sortedComments = (task.comments || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    useEffect(() => {
        const authorIds = [...new Set(sortedComments.map(c => c.authorId).filter(Boolean))];
        if (authorIds.length > 0) {
            fetchUserProfiles(authorIds);
        }
    }, [task.comments, fetchUserProfiles, currentUserProfile]);

    const scrollToBottom = () => {
        setTimeout(() => {
            commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    const handleCommentSubmit = async () => {
        if (!newComment.trim()) return;
        const commentData = {
            id: Date.now(),
            authorName: currentUserProfile?.username || 'Usuário',
            content: newComment,
            timestamp: new Date().toISOString(),
        };
        await handleAddComment(task.id, commentData);
        setNewComment('');
        showToast('Comentário adicionado!');
        scrollToBottom();
    };

    const formatTimestamp = (isoString) => {
        if (!isoString) return '';
        return new Date(isoString).toLocaleString('pt-BR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).replace('.', ',');
    };

    return (
        <div className="bg-gray-200/50 dark:bg-white/5 rounded-lg p-4">
            <h3 className="text-base font-semibold text-gray-600 dark:text-gray-300 mb-4">Comentários</h3>
            <div className="space-y-4 max-h-48 overflow-y-auto pr-2 mb-4">
                {sortedComments.length > 0 ? (
                    sortedComments.map(comment => {
                        const author = userProfiles[comment.authorId];
                        const authorName = author?.username || comment.authorName;
                        const authorAvatarUrl = author?.avatarUrl || comment.authorAvatarUrl;
                        const canDelete = currentUserProfile?.uid === comment.authorId;

                        return (
                            <div key={comment.id} className="group flex items-start gap-3 text-sm">
                                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
                                    {authorAvatarUrl ? (
                                        <img src={authorAvatarUrl} alt={authorName} className="w-full h-full object-cover" />
                                    ) : (
                                        <span>{(authorName || '..').substring(0, 2).toUpperCase()}</span>
                                    )}
                                </div>
                                <div className="flex-1 bg-black/20 p-3 rounded-lg relative">
                                    <div className="flex justify-between items-center">
                                        <p className="font-bold text-white">{authorName}</p>
                                        <p className="text-xs text-gray-500">{formatTimestamp(comment.timestamp)}</p>
                                    </div>
                                    <p className="text-gray-200 mt-1 whitespace-pre-wrap">{comment.content}</p>
                                    {canDelete && (
                                        <button 
                                            onClick={() => onDeleteRequest(task, comment)}
                                            className="absolute bottom-1 right-1 p-1 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded-full transition-all"
                                            title="Excluir comentário"
                                        >
                                            <DeleteIcon size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className="text-sm text-gray-400 text-center py-8">Nenhum comentário ainda.</p>
                )}
                <div ref={commentsEndRef} />
            </div>
            <div className="space-y-2">
                <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Adicionar um comentário..."
                    rows="3"
                    className="w-full p-2 bg-gray-100 dark:bg-black/20 rounded-lg border border-transparent focus:border-indigo-500 focus:outline-none text-sm resize-none"
                />
                <div className="text-right">
                    <GlassButton onClick={handleCommentSubmit} disabled={!newComment.trim()} className="!py-1 !px-4 !text-sm !bg-indigo-500">
                        Adicionar Comentário
                    </GlassButton>
                </div>
            </div>
        </div>
    );
};

export default CommentsWidget;